import { useMemo, useState, useEffect } from "react";
import Button from "@components/ui/Button";

// Helper to format purpose names for display
function formatPurposeName(purpose) {
  return purpose
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function QuestionPreview({ purpose, list, libraryByEnum }) {
  return (
    <div className="border rounded-lg bg-gray-50 p-4">
      <div className="text-sm font-semibold mb-2">Preview ({formatPurposeName(purpose)})</div>
      {list.length === 0 ? (
        <div className="text-sm text-gray-500">No questions added yet.</div>
      ) : (
        <ol className="list-decimal pl-5 space-y-2">
          {list.map((item, idx) => {
            const q = libraryByEnum.get(item.agent_question_enum);
            return (
              <li
                key={`${item.agent_question_enum}-${idx}`}
                className="text-sm text-gray-800"
              >
                <div className="font-medium">
                  {q?.question ?? item.agent_question_enum}
                </div>
                {q?.rules?.length ? (
                  <div className="text-xs text-gray-600 mt-0.5">
                    Rules: {q.rules.join(" • ")}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

export default function AgentQuestionsEditor({
  value,
  onChange,
  questionLibrary = [],
  purposes = [],
}) {
  // Parse purposes into default and optional
  const { defaultPurposes, optionalPurposes } = useMemo(() => {
    const defaults = [];
    const optionals = [];
    for (const p of purposes) {
      // Handle both old format (strings) and new format (objects)
      if (typeof p === "string") {
        defaults.push(p);
      } else if (p.is_optional) {
        optionals.push(p.value);
      } else {
        defaults.push(p.value);
      }
    }
    return { defaultPurposes: defaults, optionalPurposes: optionals };
  }, [purposes]);

  // Compute which optional flows have been added (key exists in value, even if empty)
  const addedOptionalPurposes = useMemo(() => {
    if (!value) return [];
    return optionalPurposes.filter((p) => p in value);
  }, [optionalPurposes, value]);

  // Available optional flows that can still be added
  const availableOptionalFlows = useMemo(() => {
    return optionalPurposes.filter((p) => !addedOptionalPurposes.includes(p));
  }, [optionalPurposes, addedOptionalPurposes]);

  // All visible tabs = default + added optional
  const visiblePurposes = useMemo(() => {
    return [...defaultPurposes, ...addedOptionalPurposes];
  }, [defaultPurposes, addedOptionalPurposes]);

  const [purpose, setPurpose] = useState(null);
  const [showAddMenu, setShowAddMenu] = useState(false);

  // Keep selected purpose valid
  useEffect(() => {
    if (!visiblePurposes.length) return;
    if (!purpose || !visiblePurposes.includes(purpose)) {
      setPurpose(visiblePurposes[0]);
    }
  }, [visiblePurposes, purpose]);

  const libraryByEnum = useMemo(() => {
    const m = new Map();
    for (const q of questionLibrary) m.set(q.agent_question_enum, q);
    return m;
  }, [questionLibrary]);

  const list = useMemo(() => {
    if (!purpose) return [];
    return (value?.[purpose] ?? [])
      .slice()
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
  }, [value, purpose]);

  const usedEnums = useMemo(
    () => new Set(list.map((x) => x.agent_question_enum)),
    [list],
  );

  const availableToAdd = useMemo(() => {
    return questionLibrary.filter((q) => !usedEnums.has(q.agent_question_enum));
  }, [questionLibrary, usedEnums]);

  const patchPurpose = (nextList) => {
    if (!purpose) return;

    const normalized = nextList.map((x, idx) => ({
      agent_question_enum: x.agent_question_enum,
      order: idx + 1,
    }));

    onChange?.({
      ...(value || {}),
      [purpose]: normalized,
    });
  };

  const move = (idx, dir) => {
    const next = [...list];
    const to = idx + dir;
    if (to < 0 || to >= next.length) return;
    [next[idx], next[to]] = [next[to], next[idx]];
    patchPurpose(next);
  };

  const removeAt = (idx) => {
    patchPurpose(list.filter((_, i) => i !== idx));
  };

  const addEnum = (agent_question_enum) => {
    if (!agent_question_enum) return;
    patchPurpose([...list, { agent_question_enum, order: list.length + 1 }]);
  };

  const addOptionalFlow = (flowPurpose) => {
    // Add the optional flow with empty questions - user will configure
    onChange?.({
      ...(value || {}),
      [flowPurpose]: [],
    });
    setPurpose(flowPurpose);
    setShowAddMenu(false);
  };

  const removeOptionalFlow = (flowPurpose) => {
    if (!optionalPurposes.includes(flowPurpose)) return; // Can't remove default flows
    const next = { ...(value || {}) };
    delete next[flowPurpose];
    onChange?.(next);
    // Switch to first visible purpose
    if (purpose === flowPurpose) {
      setPurpose(defaultPurposes[0] || null);
    }
  };

  const isOptionalPurpose = (p) => optionalPurposes.includes(p);

  if (!purposes?.length) {
    return (
      <div className="border rounded-lg bg-white p-4 text-sm text-gray-600">
        Loading purposes...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <div className="text-sm font-semibold">Agent Questions</div>
        <div className="text-xs text-gray-600 mt-1">
          Select a purpose, then build the ordered list of questions the agent
          should ask.
        </div>
      </div>

      {/* Purpose tabs with + button */}
      <div className="flex flex-wrap gap-2 items-center">
        {visiblePurposes.map((p) => (
          <div key={p} className="flex items-center">
            <button
              type="button"
              onClick={() => setPurpose(p)}
              className={`px-3 py-1.5 text-xs font-medium border transition ${
                isOptionalPurpose(p)
                  ? "rounded-l-full"
                  : "rounded-full"
              } ${
                purpose === p
                  ? "bg-gray-900 text-white border-gray-900"
                  : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
              }`}
            >
              {formatPurposeName(p)}
            </button>
            {isOptionalPurpose(p) && (
              <button
                type="button"
                onClick={() => removeOptionalFlow(p)}
                className={`px-2 py-1.5 text-xs font-medium border-t border-b border-r rounded-r-full transition ${
                  purpose === p
                    ? "bg-red-600 text-white border-red-600 hover:bg-red-700"
                    : "bg-white text-red-600 border-gray-200 hover:bg-red-50"
                }`}
                title="Remove this flow"
              >
                x
              </button>
            )}
          </div>
        ))}

        {/* Add optional flow button */}
        {availableOptionalFlows.length > 0 && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setShowAddMenu(!showAddMenu)}
              className="px-3 py-1.5 rounded-full text-xs font-medium border border-dashed border-gray-300 text-gray-600 hover:bg-gray-50 hover:border-gray-400 transition"
            >
              + Add Flow
            </button>
            {showAddMenu && (
              <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 min-w-[200px]">
                {availableOptionalFlows.map((p) => (
                  <button
                    key={p}
                    type="button"
                    onClick={() => addOptionalFlow(p)}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 first:rounded-t-lg last:rounded-b-lg"
                  >
                    {formatPurposeName(p)}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {purpose && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Left: Editor */}
          <div className="border rounded-lg bg-white">
            <div className="px-4 py-3 border-b">
              <div className="text-sm font-semibold">{formatPurposeName(purpose)} flow</div>
              <div className="text-xs text-gray-500">
                {list.length} question(s)
              </div>
            </div>

            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Add question
                </label>
                <select
                  className="w-full border rounded-lg px-3 py-2 text-sm bg-white"
                  defaultValue=""
                  onChange={(e) => {
                    addEnum(e.target.value);
                    e.target.value = "";
                  }}
                >
                  <option value="" disabled>
                    Select from library...
                  </option>
                  {availableToAdd.map((q) => (
                    <option
                      key={q.agent_question_enum}
                      value={q.agent_question_enum}
                    >
                      {q.question} ({q.agent_question_enum})
                    </option>
                  ))}
                </select>
              </div>

              <div className="border rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 text-xs font-medium text-gray-600">
                  Ordered Questions
                </div>

                {list.length === 0 ? (
                  <div className="p-3 text-sm text-gray-500">
                    No questions added.
                  </div>
                ) : (
                  <div className="divide-y">
                    {list.map((item, idx) => {
                      const q = libraryByEnum.get(item.agent_question_enum);
                      return (
                        <div
                          key={`${item.agent_question_enum}-${idx}`}
                          className="p-3 flex gap-3"
                        >
                          <div className="w-8 text-xs text-gray-500 pt-1">
                            {idx + 1}
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-gray-900">
                              {q?.question ?? item.agent_question_enum}
                            </div>
                            <div className="text-xs text-gray-500 break-all">
                              {item.agent_question_enum}
                            </div>

                            {q?.rules?.length ? (
                              <div className="mt-2 text-xs text-gray-600">
                                <span className="font-medium">Rules:</span>{" "}
                                {q.rules.join(" • ")}
                              </div>
                            ) : null}
                          </div>

                          <div className="flex flex-col gap-2">
                            <Button
                              type="button"
                              size="xs"
                              color="light"
                              onClick={() => move(idx, -1)}
                              disabled={idx === 0}
                            >
                              Move Up
                            </Button>
                            <Button
                              type="button"
                              size="xs"
                              color="light"
                              onClick={() => move(idx, +1)}
                              disabled={idx === list.length - 1}
                            >
                              Move Down
                            </Button>
                            <Button
                              type="button"
                              size="xs"
                              color="danger"
                              onClick={() => removeAt(idx)}
                            >
                              Remove
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          <QuestionPreview
            purpose={purpose}
            list={list}
            libraryByEnum={libraryByEnum}
          />
        </div>
      )}
    </div>
  );
}
